import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lightning, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const checkPolicy = (pwd) => ({
  length: pwd.length >= 8,
  upper: /[A-Z]/.test(pwd),
  digit: /\d/.test(pwd),
  special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(pwd),
});

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const policy = useMemo(() => checkPolicy(pwd), [pwd]);
  const policyOk = policy.length && policy.upper && policy.digit && policy.special;
  const match = pwd && pwd === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!policyOk) { toast.error(t("auth.password_policy_error")); return; }
    if (!match) { toast.error(t("auth.password_mismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd, new_password_confirm: confirm });
      toast.success(t("auth.reset_success"));
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-[#050505]" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-10">
          <Lightning size={28} weight="fill" className="text-[#E30613]" />
          <span className="font-display text-2xl uppercase font-black">FitAgenda <span className="text-[#E30613]">CH</span></span>
        </Link>
        <div className="border border-white/10 bg-[#0A0A0A] p-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("auth.reset_tag")}</div>
          <h1 className="font-display text-3xl uppercase font-black mt-2">{t("auth.reset_title")}</h1>
          <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{t("auth.reset_subtitle")}</p>
          {!token && <p className="text-xs text-red-400 mt-4">{t("auth.verify_missing_token")}</p>}
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.password")}</Label>
              <Input type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)}
                className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="reset-password-input" />
              {pwd && (
                <ul className="text-[10px] space-y-1 mt-2">
                  {[
                    { ok: policy.length, txt: t("auth.policy_length") },
                    { ok: policy.upper, txt: t("auth.policy_upper") },
                    { ok: policy.digit, txt: t("auth.policy_digit") },
                    { ok: policy.special, txt: t("auth.policy_special") },
                  ].map((p, i) => (
                    <li key={i} className={`flex items-center gap-2 ${p.ok ? "text-[#00FF66]" : "text-zinc-500"}`}>
                      {p.ok ? <Check size={12} weight="bold" /> : <X size={12} weight="bold" />} {p.txt}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.password_confirm")}</Label>
              <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className={`bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613] ${confirm && !match ? "border-red-500" : ""}`}
                data-testid="reset-password-confirm-input" />
            </div>
            <Button type="submit" disabled={loading || !policyOk || !match || !token}
              className="w-full bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none h-12 disabled:opacity-50"
              data-testid="reset-submit-button">
              {loading ? t("common.loading") : t("auth.reset_submit")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
